import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AuthModalSimplified from '@/components/AuthModalSimplified';
import { LogoMark } from '@/components/brand/Logo';
import { Reveal } from '@/components/brand/Reveal';
import { MobileStickyCTA } from '@/components/brand/MobileStickyCTA';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  PenLine,
  Link2,
  Gift,
  Globe2,
  Sparkles,
  Users,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';
import { HOME_HERO, imgProps } from '@/lib/imagery';
import { capture } from '@/lib/analytics';

/* -------------------------------------------------------------------------- */
/*  Content                                                                   */
/* -------------------------------------------------------------------------- */

const STEPS = [
  {
    icon: PenLine,
    title: 'Create your registry',
    sub: 'Sign up and we set the table — free, in under two minutes.',
  },
  {
    icon: Link2,
    title: 'Add anything from any store',
    sub: 'Paste a link from Amazon, FirstCry, Etsy — anywhere on the internet.',
  },
  {
    icon: Gift,
    title: 'Share and receive gifts',
    sub: 'One link to friends and family. They give. You get exactly what you need.',
  },
];

const VALUES = [
  {
    icon: Globe2,
    title: 'Add from any website',
    body: 'Amazon, FirstCry, Etsy, Instagram shops — paste any link.',
  },
  {
    icon: Sparkles,
    title: 'Smart registry suggestions',
    body: 'Pre-built picks for every trimester, by parents who\'ve been there.',
  },
  {
    icon: Users,
    title: 'Easy sharing & group gifting',
    body: 'One link. Friends can chip in together on the bigger things.',
  },
  {
    icon: ShieldCheck,
    title: 'No duplicate gifts',
    body: 'Items lock the moment they\'re claimed. No more two strollers.',
  },
];

const ASK_JB_PROMPTS = [
  'What do I actually need for the first month?',
  'Best gift for a baby shower under ₹3,000?',
  'What should I put in my registry?',
  'How do I prepare for delivery day?',
];

// Honest attribution: these are composite voices from our early-parent
// conversations, not named customers — so no invented full names. When real
// named testimonials arrive (with permission), swap them in here.
const TESTIMONIALS = [
  {
    quote:
      'It was the first time we asked for help and it didn\'t feel like asking. Everyone gave us something they actually wanted to give.',
    name: 'First-time parents',
    meta: 'Bangalore',
  },
  {
    quote:
      'Not a single duplicate. And group gifting got us the cradle we couldn\'t justify on our own.',
    name: 'Expecting couple',
    meta: 'Mumbai',
  },
  {
    quote:
      'I knew what they\'d actually use — I gave the right thing for once. Made me want one myself.',
    name: 'A gift-giver',
    meta: 'Pune',
  },
];

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

const Home = () => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);

  const createRegistry = () => {
    capture('cta_create_registry_clicked');
    navigate('/registry/new', { state: { eventType: 'Baby' } });
  };
  const exploreGifts = () => navigate('/explore-gifts');
  const askJB = (q) => navigate(q ? `/ask-jb?q=${encodeURIComponent(q)}` : '/ask-jb');

  return (
    <div className="min-h-screen bg-background text-foreground antialiased">
      <Navbar onAuthClick={() => setShowAuth(true)} />

      {/* ---------------------------------------------------------------- */}
      {/*  1. HERO                                                          */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 lg:pt-36 lg:pb-28 overflow-hidden">
        {/* Warm gradient flourish */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -right-20 h-[520px] w-[520px] rounded-full blur-3xl opacity-50"
          style={{ background: 'radial-gradient(closest-side, #F4D6CD 0%, transparent 70%)' }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-40 -left-32 h-[420px] w-[420px] rounded-full blur-3xl opacity-40"
          style={{ background: 'radial-gradient(closest-side, #DDE4D5 0%, transparent 70%)' }}
        />

        <div className="container-page relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <div className="lg:col-span-7 animate-fade-up">
            <h1 className="font-display text-display-2xl text-ink leading-[1.05]">
              Everything you need
              <br />
              for your baby,{' '}
              <span
                className="italic text-clay-700"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}
              >
                all in one place.
              </span>
            </h1>

            <p className="mt-7 max-w-xl text-lg sm:text-xl leading-relaxed text-ink-soft">
              Create your registry, add products from any store, and get exactly what you need.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={createRegistry}
                className="h-12 px-7 rounded-full bg-clay-600 hover:bg-clay-700 text-cream-50 font-medium text-[15px] shadow-soft-2 lift-on-hover"
                data-testid="hero-create-registry"
              >
                Create Your Registry
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                onClick={exploreGifts}
                variant="ghost"
                className="h-12 px-6 rounded-full text-ink hover:bg-cream-100 hover:text-clay-700 font-medium text-[15px]"
                data-testid="hero-explore-gifts"
              >
                Explore Gifts
              </Button>
            </div>

            <p className="mt-8 text-sm text-ink-muted">
              Free forever · Start at any trimester · 2-minute setup
            </p>
          </div>

          {/* Image */}
          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: '120ms' }}>
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-soft-3 ring-1 ring-black/5">
              <img
                {...imgProps(HOME_HERO)}
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>

            {/* Floating quote — adds emotional anchor to the hero */}
            <div className="hidden sm:flex absolute -bottom-8 -left-8 max-w-[280px] items-start gap-3 rounded-2xl bg-white/95 backdrop-blur-sm p-4 shadow-soft-2 ring-1 ring-black/5">
              <LogoMark size={22} className="text-clay-700 mt-0.5 shrink-0" />
              <p className="font-display text-[15px] leading-snug text-ink">
                <span className="italic">"It made the lead-up less anxious.</span> We knew exactly what we needed."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/*  1b. RETAILER TRUST STRIP                                          */}
      {/* ---------------------------------------------------------------- */}
      {/* Quiet "we work with any store" reminder set as wordmarks rather
          than logos — keeps it on-brand (no other brand's visual identity
          fighting ours) and avoids trademark risk since we're stating an
          integration capability, not claiming endorsement. */}
      <section aria-label="Stores you can add from" className="pb-8 sm:pb-12">
        <div className="container-page">
          <Reveal>
            <p className="text-center text-[11px] uppercase tracking-[0.18em] text-ink-muted mb-5">
              Add from any store you already love
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 sm:gap-x-10 text-[13px] sm:text-[14px] font-medium text-ink-soft/80">
              <span>Amazon</span>
              <span className="text-clay-300/70" aria-hidden>·</span>
              <span>FirstCry</span>
              <span className="text-clay-300/70" aria-hidden>·</span>
              <span>Mothercare</span>
              <span className="text-clay-300/70" aria-hidden>·</span>
              <span>Babyhug</span>
              <span className="text-clay-300/70" aria-hidden>·</span>
              <span>Etsy</span>
              <span className="text-clay-300/70" aria-hidden>·</span>
              <span className="italic text-ink-muted">and anywhere else on the internet</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/*  2. HOW IT WORKS — 3 steps, icon-led                              */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-b from-cream-50 via-cream-100/70 to-cream-50">
        <div className="container-page">
          <Reveal>
            <div className="max-w-2xl mb-14 sm:mb-16">
              <div className="eyebrow mb-4">How it works</div>
              <h2 className="font-display text-display-xl text-ink leading-[1.1]">
                Three steps. <span className="italic text-clay-700">Then breathe.</span>
              </h2>
            </div>
          </Reveal>

          <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal as="li" key={s.title} delay={i * 90} className="relative">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="h-12 w-12 rounded-full bg-clay-600 text-cream-50 flex items-center justify-center shadow-soft-1">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="font-display text-xl text-clay-600/70 tracking-tight">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl text-ink leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-ink-soft leading-relaxed">{s.sub}</p>
                </Reveal>
              );
            })}
          </ol>

          <div className="mt-12 sm:mt-14">
            <Button
              onClick={createRegistry}
              className="h-12 px-7 rounded-full bg-ink hover:bg-ink-soft text-cream-50 font-medium text-[15px] lift-on-hover"
            >
              Create Your Registry
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/*  3. CORE VALUE — 4 features                                       */}
      {/* ---------------------------------------------------------------- */}
      <section className="py-24 sm:py-32">
        <div className="container-page">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <div className="eyebrow mb-4">Built for new parents</div>
              <h2 className="font-display text-display-xl text-ink leading-[1.1]">
                Everything a baby registry should be —{' '}
                <span className="italic text-clay-700">and nothing it shouldn't.</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 100}>
                  <div className="group rounded-2xl bg-cream-100/70 ring-1 ring-border/70 p-7 sm:p-8 hover:bg-cream-100 hover:ring-clay-200 transition-colors duration-300 h-full">
                    <div className="h-11 w-11 rounded-full bg-clay-600/10 text-clay-700 flex items-center justify-center mb-5 group-hover:bg-clay-600 group-hover:text-cream-50 transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-xl text-ink leading-snug">{v.title}</h3>
                    <p className="mt-2 text-ink-soft leading-relaxed text-[15px]">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/*  4. ASK JB                                                        */}
      {/* ---------------------------------------------------------------- */}
      <section className="relative py-24 sm:py-32 bg-clay-700 text-cream-50 overflow-hidden">
        {/* Soft gradient seams — top & bottom bands fade cream-50 into the
            clay-700 body so the section reads as a continuous layer with the
            cream pages above & below, rather than a hard color block. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-cream-50 to-transparent opacity-25"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-cream-50 to-transparent opacity-25"
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="container-page relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-cream-50/10 ring-1 ring-cream-50/20 px-3 py-1 text-xs uppercase tracking-wider text-cream-200 mb-5">
              <Sparkles className="h-3.5 w-3.5" />
              AI parenting companion
            </div>
            <h2 className="font-display text-display-xl leading-[1.1]">
              Not sure what to add? <span className="italic">Just ask.</span>
            </h2>
            <p className="mt-5 text-lg text-cream-100/90 leading-relaxed max-w-md">
              JB is your warm, calm, free parenting companion. Trained to give clear, practical
              answers — no panic-Googling at 2 AM.
            </p>
            <div className="mt-8">
              <Button
                onClick={() => askJB()}
                className="h-12 px-7 rounded-full bg-cream-50 text-clay-700 hover:bg-cream-100 font-medium text-[15px] lift-on-hover"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                Ask JB
              </Button>
            </div>
          </Reveal>

          {/* Example prompts as deep-link chips */}
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-sm uppercase tracking-wider text-cream-200/80 mb-4">
                Try asking
              </p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ASK_JB_PROMPTS.map((q, i) => (
                <Reveal key={q} delay={120 + i * 80}>
                  <button
                    onClick={() => askJB(q)}
                    className="w-full text-left rounded-2xl bg-cream-50/10 hover:bg-cream-50/15 ring-1 ring-cream-50/15 hover:ring-cream-50/30 px-5 py-4 transition-colors duration-300 group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-[15px] text-cream-50 leading-snug">{q}</p>
                      <ArrowRight className="h-4 w-4 text-cream-200 mt-0.5 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/*  5. SOCIAL PROOF                                                  */}
      {/* ---------------------------------------------------------------- */}
      <section className="py-24 sm:py-32">
        <div className="container-page">
          <Reveal>
            <div className="max-w-2xl mb-14 text-center mx-auto">
              <div className="eyebrow mb-4">Parents on Joy Baskets</div>
              <h2 className="font-display text-display-xl text-ink leading-[1.1]">
                Real registries.{' '}
                <span className="italic text-clay-700">Real relief.</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 110}>
                <figure className="rounded-2xl bg-cream-100/70 ring-1 ring-border/70 p-7 sm:p-8 flex flex-col h-full">
                  <LogoMark size={20} className="text-clay-700 mb-5" />
                  <blockquote className="font-display text-[18px] sm:text-[19px] text-ink leading-snug flex-1">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-border/70">
                    <p className="font-medium text-ink text-[15px]">{t.name}</p>
                    <p className="text-sm text-ink-muted mt-0.5">{t.meta}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/*  6. FINAL CTA                                                     */}
      {/* ---------------------------------------------------------------- */}
      <section className="pb-24 sm:pb-32">
        <div className="container-page">
          <Reveal>
          <div className="rounded-[2rem] bg-cream-100 ring-1 ring-border/70 p-10 sm:p-16 text-center">
            <h2 className="font-display text-display-xl text-ink max-w-2xl mx-auto leading-[1.1]">
              Start your registry{' '}
              <span className="italic text-clay-700">in minutes.</span>
            </h2>
            <p className="mt-5 text-ink-soft max-w-lg mx-auto text-lg">
              Free forever, for you and your guests. No credit card. No catch.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={createRegistry}
                className="h-12 px-8 rounded-full bg-clay-600 hover:bg-clay-700 text-cream-50 font-medium text-[15px] shadow-soft-2 lift-on-hover"
                data-testid="final-create-registry"
              >
                Create Your Registry
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                onClick={exploreGifts}
                variant="ghost"
                className="h-12 px-6 rounded-full text-ink hover:bg-cream-200 font-medium text-[15px]"
              >
                Explore Gifts
              </Button>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      <Footer />

      <MobileStickyCTA />

      {showAuth && (
        <AuthModalSimplified
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          mode="signup"
          onSwitchMode={() => {}}
        />
      )}
    </div>
  );
};

export default Home;
